import axios from 'axios';
import {API_URL, MEDIA_TYPE_V2} from '../config/constants';
import SpringDataPage, {BasicPage} from '../@types/spring-data-page';
import {getErrorMessage} from '../utils/utils';
import Occurrence from '../@types/occurrence';
import Group from '../@types/group';
import {makeGroup} from '../utils/group-utils';


class OccurrenceService {
    static occurrenceUrlV2 = API_URL + 'api/v2/occurrences';

    formOccurrence(occurrence: any): Occurrence {
        let occur: Occurrence = {} as Occurrence;
        occur.id = occurrence.id;
        occur.uniqueId = occurrence.uniqueId;
        occur.displayName = occurrence.displayName;
        occur.dateTime = occurrence.dateTime;
        occur.location = occurrence.location;
        occur.created = occurrence.created;
        occur.occurrenceSource = occurrence.occurrenceSource;
        occur.occurrenceType = occurrence.occurrenceType;
        occur.rawReport = occurrence.rawReport;
        occur.group = occurrence.group ? makeGroup(occurrence.group) :  new Group();
        return occur;
    }

    formOccurrencesPagePromise(occurrencesData: any): Promise<SpringDataPage<Occurrence>> {
        let occurrencesPage: SpringDataPage<Occurrence> = {} as SpringDataPage<Occurrence>;
        if (occurrencesData) {
            if (occurrencesData._embedded?.occurrenceDTOList) {
                occurrencesPage.content = occurrencesData._embedded.occurrenceDTOList?.map((occurrence: any) => this.formOccurrence(occurrence));
            } else {
                occurrencesPage.content = [];
            }
            occurrencesPage.page = occurrencesData.page;
        }

        return Promise.resolve(occurrencesPage);
    }

    fetchOccurrences(page: BasicPage, groupId?: number|null, searchTerm?: string): Promise<SpringDataPage<Occurrence>> {
        let url = `${OccurrenceService.occurrenceUrlV2}?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;
        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formOccurrencesPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get occurrences: ' + getErrorMessage(err));
            })
    }

    fetchOccurrenceById(occurrenceId: number): Promise<Occurrence> {
        let url = `${OccurrenceService.occurrenceUrlV2}/occurrence/${occurrenceId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(this.formOccurrence(response.data));
            })
            .catch(err => {
                throw new Error(`Failed to get occurrence ${occurrenceId} ` + getErrorMessage(err));
            })
    }

    createOccurrence(occurrence: Occurrence): Promise<any> {
        return axios
            .post(`${OccurrenceService.occurrenceUrlV2}/occurrence/${occurrence.group.id}`, occurrence, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(response => {
                return this.formOccurrence(response.data);
            })
            .catch(err => {
                throw new Error('Create occurrence failed with error: ' + getErrorMessage(err));
            })
    }

    putOccurrence(occurrence: Occurrence): Promise<any> {
        return axios
            .put(`${OccurrenceService.occurrenceUrlV2}/occurrence/${occurrence.id}`, occurrence, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(response => {
                return this.formOccurrence(response.data);
            })
            .catch(err => {
                throw new Error('Create occurrence failed with error: ' + getErrorMessage(err));
            })
    }

    getOccurrenceTypes(): Promise<any> {
        return axios.get(`${OccurrenceService.occurrenceUrlV2}/occurrence-types`, {
            withCredentials: true
        }).then(response => {
            return response.data._embedded.stringList;
        }).catch(err => {
            throw new Error('Failed to get occurrence types: ' + err);
        })
    }

    deleteOccurrenceById(id: number): Promise<void> {
        return axios
            .delete(OccurrenceService.occurrenceUrlV2 + '/occurrence/' + id, {
                withCredentials: true,
                headers: { 'Content-Type': MEDIA_TYPE_V2 },
            })
            .then(() => {
                return;
            })
            .catch(err => {
                throw new Error(`Delete occurrence ${id} failed with error: ` + getErrorMessage(err));
            })
    }

    duplicateOccurrence(sourceResourceId: number | string, targetGroupId: number | string | undefined): Promise<any> {
        const data = targetGroupId !== undefined ? {
            sourceResourceId: sourceResourceId,
            targetGroupId: targetGroupId
        } : {
            sourceResourceId: sourceResourceId
        };

        return axios.post(OccurrenceService.occurrenceUrlV2 + '/copy-and-paste', data , {
            withCredentials: true,
            headers: { 'Content-Type': MEDIA_TYPE_V2 }
        }).then((response: any) => {
            return response;
        }).catch((error: any) => {
            throw new Error(`Duplicate occurrence ${sourceResourceId} failed with error: ` + getErrorMessage(error));
        });
    }
}

export default new OccurrenceService();